import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function RWD_MediaQueries() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #f472b6, #d946ef)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          📱 RWD Media Queries
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          Media queries allow you to apply CSS styles based on device width,
          height, orientation, and more.
        </p>
        <img
          src="https://www.w3schools.com/css/img_mediaqueries.gif"
          alt="Media Queries Example"
          style={{
            maxWidth: "500px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        Media queries are a cornerstone of responsive web design (RWD). They let
        your CSS respond to different screen sizes, orientations, and even
        resolutions. This allows websites to look good on desktops, tablets, and
        mobile devices without duplicating code.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fcd34d, #fbbf24)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#78350f" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Media queries use the <code>@media</code> rule in CSS.
          </li>
          <li>
            Common conditions include <code>min-width</code>,{" "}
            <code>max-width</code>, <code>orientation</code>.
          </li>
          <li>They help create layouts that adapt to different devices.</li>
          <li>
            Combine media queries with Flexbox or Grid for powerful responsive
            designs.
          </li>
          <li>Test across multiple devices or with dev tools for accuracy.</li>
        </ul>
      </div>

      {/* Example 1: Simple Media Query */}
      <h2 className="section-title">1️⃣ Simple Media Query</h2>
      <CodeCopyBlock
        code={`.box {
  width: 100%;
  padding: 20px;
  background: lightblue;
}

@media (min-width: 600px) {
  .box {
    background: lightgreen;
  }
}

@media (min-width: 900px) {
  .box {
    background: lightcoral;
  }
}`}
      />
      <p>✅ Changes the box background color based on screen width.</p>

      {/* Example 2: Layout Changes */}
      <h2 className="section-title">2️⃣ Layout Adjustments</h2>
      <CodeCopyBlock
        code={`.container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

@media (min-width: 768px) {
  .container {
    flex-direction: row;
  }
}`}
      />
      <p>✅ Switches from column layout to row layout on wider screens.</p>

      {/* Example 3: Orientation */}
      <h2 className="section-title">3️⃣ Orientation Queries</h2>
      <CodeCopyBlock
        code={`@media (orientation: portrait) {
  body {
    background: #e0f7fa;
  }
}

@media (orientation: landscape) {
  body {
    background: #ffe0b2;
  }
}`}
      />
      <p>✅ Changes background color based on device orientation.</p>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #d9f99d, #bef264)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#365314" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <code>min-width</code> and <code>max-width</code>{" "}
            breakpoints.
          </li>
          <li>Combine media queries with Grid and Flexbox layouts.</li>
          <li>
            Test your responsive designs on real devices or simulator tools.
          </li>
          <li>Try creating a responsive navigation menu or card gallery.</li>
        </ul>
      </div>
    </div>
  );
}
